import {Client, Message, StompSubscription} from '@stomp/stompjs';
import {storedAuthorization} from 'src/axiosBaseQuery/interceprtors';
import {isDevelopmentMode} from 'src/utils/helper';

/* eslint-disable no-restricted-syntax */

export class ConnectionChanel {
    parameters: {
        brokerURL: string;
        debug(str: string): void;
        reconnectDelay: number;
        heartbeatIncoming: number;
        heartbeatOutgoing: number;
    };

    listener: Client;

    subscription: StompSubscription | null;

    private topic: string | null;

    private callBack: ((mes: string) => void) | null;

    /** The token this connection was opened with, so a reconnect happens only when it changed. */
    private activatedWith: string | null = null;

    /** The current open-or-reopen, so a second request queues behind it rather than racing it. */
    private reconnecting: Promise<void> | null = null;

    constructor() {
        this.parameters = {
            brokerURL: `${process.env.REACT_APP_WS_URL}`,
            debug() {
                // WebSocket debug logs disabled
            },
            reconnectDelay: 5000,
            heartbeatIncoming: 10000,
            heartbeatOutgoing: 10000,
        };
        this.subscription = null;
        this.topic = null;
        this.callBack = null;

        this.listener = new Client(this.parameters);

        this.listener.onConnect = () => {
            // Subscribe here so it (re)runs on every (re)connect. Relying on an external
            // `connected` check races the async handshake and silently skips the subscription.
            this.subscribeToTopic();
        };

        this.listener.onWebSocketClose = () => {
            // Drop the stale subscription so onConnect re-subscribes after a reconnect
            this.subscription = null;
        };

        this.listener.onStompError = (frame) => {
            // Will be invoked in case of error encountered at Broker
            // Bad login/passcode typically will cause an error
            // Complaint brokers will set a ` message ` header with a brief message. Body may contain details.
            // Compliant brokers will terminate the connection after any error
            isDevelopmentMode() && console.log(`Broker reported error: ${frame.headers.message}`);
            isDevelopmentMode() && console.log(`Additional details: ${frame.body}`);
        };

        this.listener.onDisconnect = (frame) => {
            isDevelopmentMode() && console.log(`onDisconnect: ${frame}`);
        };
    }

    /**
     * Reconnects with whatever token is stored right now.
     *
     * The header goes on at activate rather than at construction: the channel is built before anyone
     * has signed in, and it is reused across a sign-out and the next sign-in. Reading the token here
     * means a reconnect carries the current one instead of whichever was in hand at start-up.
     *
     * The server refuses a personal topic to a connection that arrives without one, so a stale or
     * missing token surfaces as a refused subscription rather than as somebody else's data.
     */
    activateChannel(): Promise<void> {
        // Chained rather than fired: deactivate() resolves when the socket has actually closed, and
        // activating before it does throws "Still DEACTIVATING". Two calls arriving together queue
        // instead of racing, which is what a sign-in during a reconnect looks like.
        this.reconnecting = (this.reconnecting ?? Promise.resolve())
            .then(async () => {
                // `active` rather than `connected`: a socket still opening has to be closed too, and
                // it answers false to the second.
                if (this.listener.active) {
                    await this.listener.deactivate();
                    this.subscription = null;
                }
                const authorization = storedAuthorization();
                this.listener.connectHeaders = authorization ? {authorization} : {};
                this.activatedWith = authorization;
                this.listener.activate();
            })
            // A failed attempt must not poison the chain, or nothing reconnects for the rest of the
            // session. The client retries on its own; this only records why.
            .catch((error) => {
                isDevelopmentMode() && console.log(`Could not (re)open the channel: ${error}`);
            });
        return this.reconnecting;
    }

    /**
     * Reopens the connection when the stored token is not the one it was opened with.
     *
     * The channel is created before anybody signs in, so the first connection is anonymous - and an
     * anonymous connection is refused a personal topic, which is the point of the change on the
     * server. Signing in has to be followed by a reconnect carrying the token, and signing out by one
     * that no longer does.
     */
    ensureAuthenticated() {
        if (storedAuthorization() !== this.activatedWith) {
            void this.activateChannel();
        }
    }

    onSubscribe(topic: string, callBack: (mes: string) => void) {
        this.topic = topic;
        this.callBack = callBack;
        this.subscribeToTopic();
    }

    /**
     * A personal topic is refused to a connection carrying no token, and the broker closes the session
     * on any error - so the client reconnects five seconds later, subscribes again, and is refused
     * again, for as long as the tab is open. Twelve handshakes a minute, each a thread on the server,
     * all of them certain to fail.
     *
     * Guest topics are the exception the server makes deliberately: a guest at a public charger has no
     * account and cannot have a token, so `/topic/guest-` stays open to anonymous subscribers. The rule
     * here mirrors StompAuthInterceptor rather than guessing at it.
     */
    private mayAttempt(topic: string) {
        return topic.startsWith('guest-') || !!storedAuthorization();
    }

    private subscribeToTopic() {
        if (this.topic && !this.mayAttempt(this.topic)) {
            return;
        }
        if (this.listener.connected && this.topic && this.callBack && !this.subscription) {
            const callBack = this.callBack;
            this.subscription = this.listener.subscribe(`/topic/${this.topic}`, (message: Message) => {
                message.body && callBack(message.body);
            });
        }
    }

    onUnSubscribe() {
        this.subscription?.unsubscribe();
        this.subscription = null;
    }

    onDeactivate() {
        // Only deactivate an established connection. StrictMode mounts twice
        // (mount->unmount->mount); deactivating a still-connecting client aborts the
        // async _connect (it bails on `!active`) so the socket never opens.
        if (this.listener?.connected) {
            this.listener.deactivate();
        }
    }
}
